import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import PropdocsModal from '../../../../modal/PropdocsModal';
import { ModalFormLayout, FormSection, FormField } from '../../../../shared/form-components';
import { Checkbox } from '../../../../checkbox-or-radio-field';
import { queriesV2 } from '../../../../../api';
import styles from './styles.module.scss';

interface DealDocumentPickerModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: (selectedDocumentIds: string[]) => void;
  dealId: string;
  currentDocumentId: string;
  alreadyLinkedDocumentIds: string[];
  loading?: boolean;
}

function getStatusPillClass(status?: string): string {
  if (!status) return '';
  const s = status.toLowerCase();
  if (['drafting', 'reviewing'].includes(s)) return styles.statusPill__blue;
  if (['executed', 'active'].includes(s)) return styles.statusPill__green;
  if (['terminated'].includes(s)) return styles.statusPill__red;
  if (['archived', 'inactive'].includes(s)) return styles.statusPill__gray;
  if (['shared', 'signing', 'preparing to sign'].includes(s)) return styles.statusPill__orange;
  return styles.statusPill__gray;
}

const DealDocumentPickerModal = ({
  show,
  onClose,
  onConfirm,
  dealId,
  currentDocumentId,
  alreadyLinkedDocumentIds,
  loading = false,
}: DealDocumentPickerModalProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data, loading: queryLoading } = useQuery(queriesV2.getDealDocumentsForPicker, {
    variables: { dealId },
    skip: !show || !dealId,
    fetchPolicy: 'network-only',
  });

  const availableDocuments = useMemo(() => {
    if (!data?.getDealById?.documents) return [];
    return data.getDealById.documents.filter(
      (doc) => doc.id !== currentDocumentId && !alreadyLinkedDocumentIds.includes(doc.id)
    );
  }, [data, currentDocumentId, alreadyLinkedDocumentIds]);

  const toggleDocument = (docId: string) => {
    setSelectedIds((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedIds.length === 0) return;
    onConfirm(selectedIds);
    setSelectedIds([]);
  };

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  return (
    <PropdocsModal
      size="md"
      show={show}
      setShow={handleClose}
      title={<FormattedMessage id="modal.attach-deal-document" defaultMessage="Attach Deal Document" />}
    >
      <ModalFormLayout onSubmit={handleConfirm}>
        <ModalFormLayout.Body>
          <FormSection>
            <FormField label="Select documents to attach">
              {queryLoading && (
                <div className="text-muted py-3 text-center">Loading documents...</div>
              )}
              {!queryLoading && availableDocuments.length === 0 && (
                <div className="text-muted py-3 text-center">No available documents to link.</div>
              )}
              {!queryLoading && availableDocuments.length > 0 && (
                <div className={styles.dealDocPickerList}>
                  {availableDocuments.map((doc) => (
                    <div key={doc.id} className={styles.dealDocPickerItem}>
                      <Checkbox
                        id={`deal-doc-${doc.id}`}
                        checked={selectedIds.includes(doc.id)}
                        onChange={() => toggleDocument(doc.id)}
                        label={
                          <span className={styles.dealDocPickerLabel}>
                            <span className={styles.dealDocPickerItemTitle}>{doc.title}</span>
                            <span className={`${styles.statusPill} ${getStatusPillClass(doc.status)}`}>
                              {doc.status}
                            </span>
                          </span>
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </FormField>
          </FormSection>
        </ModalFormLayout.Body>

        <ModalFormLayout.Footer>
          <Button variant="link" className="text-secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            type="submit"
            disabled={selectedIds.length === 0 || loading}
          >
            {loading && <span className="spinner-border spinner-border-sm me-2" />}
            {`Attach${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
          </Button>
        </ModalFormLayout.Footer>
      </ModalFormLayout>
    </PropdocsModal>
  );
};

export default DealDocumentPickerModal;
